import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, collection } from 'firebase/firestore';
import { db } from '../../firebase';
import Alert from '../ui/Alert';
import Button from '../ui/Button';
import LiveArenaLobby from '../home/LiveArenaLobby';
import CustomWagerCreateModal from '../features/CustomWagerCreateModal';
import {
  acceptOpenChallengeTransaction,
  buildOpenChallengeRef,
  cancelOpenChallenge,
  revertOpenChallengeAccept,
  subscribeGroupOpenChallenges,
  subscribeMyGroupOpenChallenges,
  writeOpenChallenge,
} from '../../api/openChallengeService';
import { CHALLENGES_COLLECTION } from '../../api/challengeService';
import { deductWalletStake, refundWalletStake } from '../../api/walletApi';
import { fetchUserProfile } from '../../api/userProfileService';
import { inferOpponentSide } from '../../utils/customPropSides';
import { canUseCustomWagers } from '../../utils/subscriptionAccess';
import { MAX_WAGER_PER_PLAYER } from '../../api/walletService';
import PlusLeagueUpsellSheet from './PlusLeagueUpsellSheet';
import { CHECKOUT_SOURCES } from '../../constants/checkoutAttribution';
import { usePlusCheckout } from '../../hooks/usePlusCheckout';

export default function GroupOpenArenaSection({
  groupId,
  groupName,
  userId,
  userName,
  subscriptionProfile,
  postLockSignal = 0,
}) {
  const [openLocks, setOpenLocks] = useState([]);
  const [myLocks, setMyLocks] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState('');
  const [acceptBusyId, setAcceptBusyId] = useState(null);
  const [cancelBusyId, setCancelBusyId] = useState(null);
  const [customDescription, setCustomDescription] = useState('');
  const [customCreatorPick, setCustomCreatorPick] = useState('');
  const [customStake, setCustomStake] = useState(10);
  const [customWagerError, setCustomWagerError] = useState('');
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellVariant, setUpsellVariant] = useState('group_lock_post');

  const userCanPost = canUseCustomWagers(subscriptionProfile);
  const { startPlusCheckout, checkoutLoading } = usePlusCheckout();

  const openLockUpsell = (variant) => {
    setUpsellVariant(variant);
    setUpsellOpen(true);
  };

  useEffect(() => {
    if (!groupId) return undefined;
    return subscribeGroupOpenChallenges(groupId, setOpenLocks, () => setOpenLocks([]));
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !userId) return undefined;
    return subscribeMyGroupOpenChallenges(userId, groupId, setMyLocks, () => setMyLocks([]));
  }, [groupId, userId]);

  useEffect(() => {
    if (postLockSignal <= 0) return;
    if (!userCanPost) {
      openLockUpsell('group_lock_post');
      return;
    }
    setCustomWagerError('');
    setCreateOpen(true);
  }, [postLockSignal, userCanPost]);

  const recentlyMatched = useMemo(() => {
    const seen = new Set();
    const combined = [...myLocks, ...openLocks];
    return combined
      .filter((c) => {
        if (c.status !== 'matched' || seen.has(c.id)) return false;
        seen.add(c.id);
        return c.creatorId === userId || c.acceptedBy === userId;
      })
      .sort((a, b) => {
        const aMs = a.acceptedAt?.toMillis?.() ?? a.acceptedAt ?? 0;
        const bMs = b.acceptedAt?.toMillis?.() ?? b.acceptedAt ?? 0;
        return bMs - aMs;
      })
      .slice(0, 10);
  }, [myLocks, openLocks, userId]);

  const handleRunItBack = useCallback((challenge) => {
    const wasCreator = challenge.creatorId === userId;
    setCustomDescription(challenge.wager || '');
    setCustomCreatorPick(
      wasCreator ? (challenge.creatorSide || '') : (challenge.opponentSide || ''),
    );
    setCustomStake(Number(challenge.stake) || 10);
    setCustomWagerError('');
    setCreateOpen(true);
  }, [userId]);

  const handleCreate = useCallback(async () => {
    if (!userCanPost) {
      openLockUpsell('group_lock_post');
      return;
    }
    const description = customDescription.trim();
    const creatorPick = customCreatorPick.trim();
    const stake = Number(customStake);
    if (!description || !creatorPick || stake <= 0) {
      setCustomWagerError('Fill in the prop, your side, and stake.');
      return;
    }
    if (!userId) return;

    const ref = buildOpenChallengeRef();
    const openId = ref.id;
    setCustomWagerError('');
    try {
      await writeOpenChallenge(ref, {
        creatorId: userId,
        creatorUsername: userName || 'Player',
        wager: description,
        creatorSide: creatorPick,
        opponentSide: inferOpponentSide(creatorPick),
        stake,
        groupId,
        groupName,
      });
      try {
        await deductWalletStake(stake, groupId ? 'Group lock entry' : 'Open arena entry', openId);
      } catch (walletErr) {
        await cancelOpenChallenge(openId, userId);
        throw walletErr;
      }
      setCreateOpen(false);
      setCustomDescription('');
      setCustomCreatorPick('');
      setCustomStake(10);
    } catch (err) {
      setCustomWagerError(err?.message || 'Could not post lock.');
    }
  }, [
    customCreatorPick,
    customDescription,
    customStake,
    groupId,
    groupName,
    userCanPost,
    userId,
    userName,
  ]);

  const handleAccept = useCallback(async (openChallenge) => {
    if (!userCanPost) {
      openLockUpsell('group_lock_accept');
      return;
    }
    if (openChallenge.creatorId === userId) return;
    const stake = Number(openChallenge.stake);
    if (!stake) return;

    setAcceptBusyId(openChallenge.id);
    setError('');
    const challengeRef = doc(collection(db, CHALLENGES_COLLECTION));
    const challengeId = challengeRef.id;

    try {
      const [creatorProfile, acceptorProfile] = await Promise.all([
        fetchUserProfile(openChallenge.creatorId),
        fetchUserProfile(userId),
      ]);
      await acceptOpenChallengeTransaction({
        openChallengeId: openChallenge.id,
        acceptorId: userId,
        acceptorName: userName || 'Player',
        acceptorRiskTally: acceptorProfile?.riskTally ?? 0,
        creatorRiskTally: creatorProfile?.riskTally ?? 0,
        challengeRef,
      });
      try {
        await deductWalletStake(stake, 'Group lock accept entry', challengeId);
      } catch (walletErr) {
        await revertOpenChallengeAccept({
          openChallengeId: openChallenge.id,
          challengeId,
          actorId: userId,
        });
        throw walletErr;
      }
    } catch (err) {
      setError(err?.message || 'Could not accept lock.');
    } finally {
      setAcceptBusyId(null);
    }
  }, [userCanPost, userId, userName]);

  const handleCancel = useCallback(async (openId) => {
    setCancelBusyId(openId);
    setError('');
    try {
      const open = myLocks.find((c) => c.id === openId) || openLocks.find((c) => c.id === openId);
      const stake = Number(open?.stake) || 0;
      await cancelOpenChallenge(openId, userId);
      if (stake > 0) {
        await refundWalletStake(stake, 'Group lock cancel refund', openId);
      }
    } catch (err) {
      setError(err?.message || 'Could not cancel lock.');
    } finally {
      setCancelBusyId(null);
    }
  }, [myLocks, openLocks, userId]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-violet-400">Group locks</p>
          <p className="text-sm text-gray-500 font-medium">Private open locks for league members only.</p>
        </div>
        <Button
          type="button"
          variant="admin"
          size="sm"
          onClick={() => {
            if (!userCanPost) {
              openLockUpsell('group_lock_post');
              return;
            }
            setCustomWagerError('');
            setCreateOpen(true);
          }}
        >
          {userCanPost ? 'Post lock' : 'Post lock — Plus'}
        </Button>
      </div>

      {!userCanPost && (
        <button
          type="button"
          onClick={() => openLockUpsell('group_lock_post')}
          className="text-xs font-bold text-violet-400 hover:text-violet-300 text-left"
        >
          Group locks are a Plus feature — see what you unlock →
        </button>
      )}

      {error && <Alert tone="error" className="text-xs font-bold">{error}</Alert>}

      <LiveArenaLobby
        id="group-open-arena"
        openChallenges={openLocks}
        myOpenLocks={myLocks}
        recentlyMatched={recentlyMatched}
        currentUserId={userId}
        canUseCustomWagers={userCanPost}
        onCreateLock={() => {
          if (!userCanPost) openLockUpsell('group_lock_post');
          else setCreateOpen(true);
        }}
        onRunItBack={userCanPost ? handleRunItBack : undefined}
        hideMatchedStake
        onJoin={handleAccept}
        onCancel={handleCancel}
        acceptBusyId={acceptBusyId}
        cancelBusyId={cancelBusyId}
      />

      <CustomWagerCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        customWagerError={customWagerError}
        customDescription={customDescription}
        onDescriptionChange={setCustomDescription}
        customCreatorPick={customCreatorPick}
        onCreatorPickChange={setCustomCreatorPick}
        customStake={customStake}
        onStakeChange={setCustomStake}
        maxWagerPerPlayer={MAX_WAGER_PER_PLAYER}
        customOpponentId=""
        onOpponentChange={() => {}}
        challengeMode="open"
        onChallengeModeChange={() => {}}
        wagerEligibleFriends={[]}
        blockedPlayers={[]}
        playerStats={{}}
        getRiskTally={() => 0}
        escrowName="locked funds"
        subscriptionProfile={subscriptionProfile}
        onSubmit={handleCreate}
        submitDisabled={!userCanPost}
        groupContext={{ id: groupId, name: groupName }}
      />

      <PlusLeagueUpsellSheet
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        variant={upsellVariant}
        checkoutLoading={checkoutLoading}
        onStartTrial={() => startPlusCheckout({
          source: upsellVariant === 'group_lock_accept'
            ? CHECKOUT_SOURCES.GROUP_LOCK_ACCEPT
            : CHECKOUT_SOURCES.GROUP_LOCK_POST,
          detail: groupId,
        })}
      />
    </div>
  );
}
